import { clsx } from 'clsx'
import { useLayoutVariant } from '../../hooks/useLayoutVariant'

export default function Table({ children, className = '' }) {
  const { isAdminAppUI } = useLayoutVariant()

  return (
    <div className={clsx(
      isAdminAppUI
        ? 'overflow-x-auto overflow-y-visible shadow-none ring-1 ring-[#243246] rounded-[12px] relative bg-[#1a2535]'
        : 'overflow-x-auto overflow-y-visible shadow-soft ring-1 ring-pastel-pink dark:ring-gray-700 rounded-3xl relative pb-10 bg-white',
      '-mx-3 sm:mx-0', // Remove horizontal margin on mobile
      className
    )}>
      <div className="inline-block min-w-full align-middle">
        <table className={clsx('min-w-full divide-y divide-gray-200 dark:divide-gray-700', className)}>
          {children}
        </table>
      </div>
    </div>
  )
}

export function TableHeader({ children, className = '' }) {
  const { isAdminAppUI } = useLayoutVariant()
  return (
    <thead className={clsx(
      isAdminAppUI ? 'bg-[#1f2d40]' : 'bg-pastel-cream dark:bg-gray-800/50',
      className
    )}>
      {children}
    </thead>
  )
}

export function TableBody({ children, className = '' }) {
  const { isAdminAppUI } = useLayoutVariant()
  return (
    <tbody className={clsx(
      isAdminAppUI
        ? 'bg-[#1a2535] divide-y divide-[#243246]'
        : 'bg-white dark:bg-gray-800 divide-y divide-pastel-pink dark:divide-gray-700',
      className
    )}>
      {children}
    </tbody>
  )
}

export function TableRow({ children, className = '', hover = true, onClick }) {
  const { isAdminAppUI } = useLayoutVariant()
  return (
    <tr 
      onClick={onClick}
      className={clsx(
        hover && (isAdminAppUI
          ? 'hover:bg-[rgba(0,200,150,0.06)] transition-all duration-200 cursor-pointer'
          : 'hover:bg-pastel-cream/60 dark:hover:bg-gray-700/50 transition-all duration-300 cursor-pointer hover:shadow-sm'),
        !hover && (isAdminAppUI ? 'bg-[#1a2535]' : 'bg-white dark:bg-gray-800'),
        onClick && 'cursor-pointer active:scale-[0.98]',
        className
      )}
    >
      {children}
    </tr>
  )
}

export function TableCell({ children, className = '', header = false, align = 'left' }) {
  const { isAdminAppUI } = useLayoutVariant()
  const alignClasses = {
    left: 'text-left',
    right: 'text-right',
    center: 'text-center',
  }

  const baseClasses = header
    ? (isAdminAppUI
      ? 'px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-[11px] font-medium text-[#7a8fa6] uppercase tracking-[0.04em]'
      : 'px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-300 uppercase tracking-wider')
    : (isAdminAppUI
      ? 'px-3 sm:px-4 md:px-6 py-3 sm:py-3 whitespace-nowrap text-sm text-[#e8edf5]'
      : 'px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-pastel-ink dark:text-gray-100')

  return (
    <td className={clsx(baseClasses, alignClasses[align], 'relative', className)}>
      {children}
    </td>
  )
}

export function TableHead({ 
  children, 
  className = '', 
  sortable = false,
  sortDirection,
  onSort,
  align = 'left'
}) {
  const { isAdminAppUI } = useLayoutVariant()
  const alignClasses = {
    left: 'text-left',
    right: 'text-right',
    center: 'text-center',
  }

  return (
    <th 
      className={clsx(
        isAdminAppUI
          ? 'px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-[11px] font-medium text-[#7a8fa6] uppercase tracking-[0.04em]'
          : 'px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider',
        alignClasses[align],
        sortable && (isAdminAppUI
          ? 'cursor-pointer hover:bg-[rgba(0,200,150,0.06)] transition-colors select-none'
          : 'cursor-pointer hover:bg-pastel-cream dark:hover:bg-gray-700 transition-colors select-none'),
        className
      )}
      onClick={sortable ? onSort : undefined}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortable && (
          <div className="flex flex-col -space-y-1">
            <svg 
              className={clsx(
                'w-3 h-3',
                sortDirection === 'asc' ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'
              )} 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            <svg 
              className={clsx(
                'w-3 h-3 -mt-1',
                sortDirection === 'desc' ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'
              )} 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>
    </th>
  )
}
